'use client';

import React, { createContext, useState, useEffect, useContext } from 'react';
import { ShibYardService, Land } from '../services/Crypto/ShibYard/ShibYardService';
import { LeashService, LeashInfo } from '../services/Crypto/Leash/LeashService';
import { LockShiboshiService, ShiboshiInfo } from '../services/Crypto/Shiboshi/LockShiboshiService';
import { getWalletClient } from '@wagmi/core';
import { config } from "../contexts/wagmi-provider"
import { useAccount } from 'wagmi';
import useClaimRewards from '../hooks/useClaimRewards';
import { mainNetworkChainId } from '../constants';

interface UserInfoContextProps {
  lands: Array<Land>;
  nLands: number;
  lockedLeash: LeashInfo;
  unlockLeash(): Promise<void>;
  lockedShiboshi: ShiboshiInfo;
  unlockShiboshi(): Promise<void>;
  refresh(): Promise<void>;
  refreshLockedLeash(): Promise<void>;
  refreshLockedShiboshi(): Promise<void>;
  refreshLands(): Promise<void>;
  refreshClaimableBone(): Promise<void>;
  claimAllBone(): Promise<void>;
  claimableBoneAmount: number;
}
const initialContext: UserInfoContextProps = {
  lands: [],
  nLands: 0,
  lockedLeash: {} as LeashInfo,
  unlockLeash: (): Promise<void> => {
    return new Promise(() => { });
  },
  lockedShiboshi: {} as ShiboshiInfo,
  unlockShiboshi: (): Promise<void> => {
    return new Promise(() => { });
  },
  refresh: (): Promise<void> => {
    return new Promise(() => { });
  },
  refreshLockedLeash: (): Promise<void> => {
    return new Promise(() => { });
  },
  refreshLockedShiboshi: (): Promise<void> => {
    return new Promise(() => { });
  },
  refreshLands: (): Promise<void> => {
    return new Promise(() => { });
  },
  refreshClaimableBone: (): Promise<void> => {
    return new Promise(() => { });
  },
  claimAllBone: (): Promise<void> => {
    return new Promise(() => { });
  },
  claimableBoneAmount: 0,
};
export const UserInfoContext = createContext<UserInfoContextProps>(initialContext);
export const UserInfoProvider = ({ children }: any) => {
  const [lands, setLands] = useState<Land[]>([]);
  const [nLands, setNLands] = useState<number>(0);
  const [lockedLeash, setLockedLeash] = useState<LeashInfo>({} as LeashInfo);
  const [lockedShiboshi, setLockedShiboshi] = useState<ShiboshiInfo>({} as ShiboshiInfo);
  const { address, chain } = useAccount()
  const { claimData, claimCallback } = useClaimRewards();
  const [claimableBoneAmount, setClaimableBoneAmount] = useState<number>(0);

  useEffect(() => {
    if (claimData) {
      setClaimableBoneAmount(Number(claimData.unclaimedAmount));
    }
  }, [claimData]);

  useEffect(() => {
    const fetchData = async () => {
      await refresh(); // Call the refresh function and wait for it to complete
    };

    fetchData(); // Invoke the async function
  }, [address, chain?.id]);


  /* typeof window !== 'undefined' && (window as any).ethereum !== undefined
      ? (window as any).ethereum
      : publicClient?.transport?.transports[0].value.url; */

  const unlockLeash = async (): Promise<void> => {
    if (address) {
      const _provider = await getWalletClient(config)
      const receipt = await LeashService.unlock(address, _provider);
      if (receipt?.status as boolean) refreshLockedLeash();
    }
  };

  const unlockShiboshi = async (): Promise<void> => {
    if (address) {
      const _provider = await getWalletClient(config);
      const receipt = await LockShiboshiService.unlock(address, _provider);
      if (receipt?.status as boolean) refreshLockedShiboshi();
    }
  };
  const claimAllBone = async (): Promise<void> => {
    if (claimCallback) {
      const txHash = await claimCallback();
      if (txHash) {
        console.log('Claim successful, transaction hash:', txHash);
        refreshClaimableBone()
        // Optionally refresh or update state here
      }
    }
  };

  const refreshLockedLeash = async (): Promise<void> => {
    if (address) {
      const _provider = await getWalletClient(config);
      try {
        const userLockedLeash = await LeashService.lockInfoOf(address, _provider);
        setLockedLeash(userLockedLeash);
      } catch { }
    }
  };

  const refreshLockedShiboshi = async (): Promise<void> => {
    if (address && chain?.id == mainNetworkChainId) {
      const _provider = await getWalletClient(config)
      try {
        const userLockedShiboshi = await LockShiboshiService.lockInfoOf(
          address,
          _provider
        );
        setLockedShiboshi(userLockedShiboshi);
      } catch { }
    }
  };

  const refreshClaimableBone = async (): Promise<void> => {
    if (claimData) {
      try {
        setClaimableBoneAmount(Number(claimData.unclaimedAmount));
      } catch (error) {
        console.error('Error refreshing claimable bone amount:', error);
      }
    }
  };

  const refreshLands = async (): Promise<void> => {
    if (address) {
      try {
        const userLands = await ShibYardService.getAllLandsByWallet(address.toLowerCase());
        setLands(userLands);
        setNLands(userLands.length);
      } catch { }
    }
  };

  const refresh = async (): Promise<void> => {
    if (address) {
      if (chain?.id == mainNetworkChainId) {
        await refreshLockedLeash(); // Await the promise
        await refreshLockedShiboshi(); // Await the promise
        await refreshClaimableBone(); // Await the promise
      }
      await refreshLands(); // Await the promise
    }
  };

  return (
    <UserInfoContext.Provider
      value={{
        lands,
        nLands,
        lockedLeash,
        lockedShiboshi,
        refresh,
        unlockLeash,
        unlockShiboshi,
        refreshLockedLeash,
        refreshLockedShiboshi,
        refreshLands,
        refreshClaimableBone,
        claimAllBone,
        claimableBoneAmount,
      }}
    >
      {children}
    </UserInfoContext.Provider>
  );
};

export const useUserInfoContext = () => useContext(UserInfoContext);
